import { useLazoAIChat } from "@/contexts/FilterConversationContext";
import { useSelectedConversation } from "@/contexts/SelectedConversationContext";
import { Colors } from "@/enums/Colors";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { useResponsive } from "@/hooks/useResponsive";
import { LazoUser } from "@/models/LazoUser";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { ConversationTypePath } from "@/types/ConversationType";
import { AddOutlined, CloseOutlined, Inventory2Outlined, MoreHorizOutlined, SearchOutlined } from "@mui/icons-material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Box, Button, Menu, MenuItem, MenuProps, Tab, Tabs, Typography, alpha, styled } from "@mui/material";
import { ApiError } from "next/dist/server/api-utils";
import { Dispatch, RefObject, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import UserManage from "../../../assets/images/UserManage.svg";
import UserPlus from "../../../assets/images/UserPlus.svg";
import InfoLogo from "../../../assets/images/info-logo.svg";
import LeaveLogo from "../../../assets/images/leave-logo.svg";
import StarLogo from "../../../assets/images/star-logo.svg";
import CommonButton from "../utils/CommonButton";
import SearchField from "../utils/SearchField";
import ConversationFilter from "./ConversationFilter";
import ConversationsList from "./ConversationsList";
import { LazoAIChatContainer } from "./LazoAIChatContainer";
import ManageUserModal from "./ManageUserModal";
import UserListModal from "./drawer-components/UserListModal";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiTypography-root": {
    fontWeight: "500",
  },
  "& .MuiPaper-root": {
    border: `1px solid ${Colors.LIGHT_GRAY}`,
    borderRadius: 8,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: Colors.LIGHT_GRAY_8E8EA0,
    boxShadow: "0px 1px 4px 0px #00000040",
    "& .MuiMenu-list": {
      padding: "0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 22,
        marginRight: theme.spacing(1.5),
      },
      "&.Mui-focusVisible": {
        backgroundColor: alpha(Colors.ALMOST_WHITE, 1),
      },
    },
  },
}));

const MainContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "conversationListOpen" && prop !== "isMobile",
})<{ conversationListOpen: boolean; isMobile?: boolean }>(({ conversationListOpen, isMobile }) => ({
  position: "sticky",
  top: 0,
  padding: 0,
  backgroundColor: Colors.WHITE,
  display: "flex",
  flexFlow: "column",
  gap: 0,
  zIndex: 1003,
  transition: "padding 0.2s ease, gap 0.2s ease",
  boxShadow: !conversationListOpen ? `0 0 4px ${Colors.STONE_GRAY}` : "none",
  "& .back-row, & .header-controls": {
    maxHeight: 0,
    overflow: "hidden",
    transition: "max-height 0.2s ease",
  },
  "&:hover": {
    padding: isMobile ? "0.75rem" : "1rem 0.8rem 0.5rem 0.8rem",
    gap: isMobile ? "0.75rem" : "0.5rem",
  },
  "&:hover .back-row": {
    maxHeight: "60px",
  },
  "&:hover .header-controls": {
    maxHeight: "120px",
  },
}));

const IconsContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  alignItems: "center",
  gap: "0.5rem",
  "& .MuiSvgIcon-root": {
    color: Colors.ELEGANT_BLACK,
  },
  "& .MuiInputAdornment-root": {
    "& .MuiSvgIcon-root": {
      color: Colors.LIGHT_GRAY_CDCDCD,
    },
  },
});

const ConversationsButton = styled(CommonButton)({
  display: "flex",
  justifyContent: "start",
  width: "100%",
  borderRadius: "0.5rem",
  marginBottom: "0.5rem",
  alignItems: "center",
  gap: "0.5rem",
  minHeight: "36px",
  overflow: "hidden",
  backgroundColor: Colors.WHITE,
  color: Colors.BLACK,
  ":hover": {
    backgroundColor: Colors.WHITE,
  },
});

const ConversationContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "0.5rem",
  padding: "0.25rem 0.5rem",
  overflow: "hidden",
  width: "100%",
});

export const ConversationTitle = styled(Typography)({
  textWrap: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  textAlign: "start",
  borderRadius: ".5rem",
  flex: 1,
  minWidth: 0,
});

export const NewConversationButton = styled(CommonButton)({
  borderRadius: "100%",
  width: "2rem !important",
  height: "2rem !important",
  minWidth: "0 !important",
  minHeight: "0 !important",
});

const SecondaryButton = styled(CommonButton)({
  borderRadius: "0.5rem",
  height: "30px",
  padding: "0 0.875rem",
  marginBottom: "0.5rem",
  gap: "0.5rem",
  backgroundColor: Colors.BUTTON_SECONDARY_PURPLE,
  color: Colors.LAZO_PURPLE,
  ":hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
  },
});

const ChatTabs = styled(Tabs)({
  width: "100%",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY}`,
  "& .MuiTabs-indicator": {
    backgroundColor: `${Colors.ELEGANT_BLACK}`,
  },
  "& .MuiButtonBase-root": {
    textTransform: "none",
    "&.Mui-selected": {
      color: Colors.ELEGANT_BLACK,
    },
  },
});

const ArchivedChatButton = styled(Button)({
  padding: "0",
  maxWidth: "28px !important",
  height: "28px",
  minWidth: "28px",
  borderRadius: "100%",
  "&.archived-open": {
    "& .MuiSvgIcon-root": { color: "white" },
    backgroundColor: `${Colors.LAZO_PURPLE} !important`,
  },
});

export enum ChatsCategory {
  MY_CHATS = "MY_CHATS",
  GUEST_CHATS = "GUEST_CHATS",
}

interface LazoAIChatDrawerContentProps {
  lazoUser: LazoUser;
  setIsOpenDrawer: Dispatch<SetStateAction<boolean>>;
  conversationPromise?: (tenantExternalId: string) => Promise<ApiResponseV2<LazoChatConversation>>;
}

export default function LazoAIChatDrawerContent({ lazoUser, setIsOpenDrawer, conversationPromise }: LazoAIChatDrawerContentProps) {
  const { t } = useTranslation("chat");

  const { isMobile, isTablet } = useResponsive();
  const isMobileView = isMobile || isTablet;

  const [openManageUsersModal, setOpenManageUsersModal] = useState<boolean>(false);
  const [conversationHasMembers, setConversationHasMembers] = useState<boolean>(false);
  const [showChatMembersModal, setShowChatMembersModal] = useState<boolean>(false);
  const [isConversationListOpen, setIsConversationListOpen] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingArchived, setIsLoadingArchived] = useState<boolean>(false);
  const [toggleFilter, setToggleFilter] = useState<boolean>(false);

  const [selectedChatsCategory, setSelectedChatsCategory] = useState<ChatsCategory>(ChatsCategory.MY_CHATS);

  const [chatGuestActionMenu, setChatGuestActionMenu] = useState<null | SVGSVGElement>(null);
  const openMenu = Boolean(chatGuestActionMenu);
  const { scrollToMessageExternalId, setScrollToMessageExternalId, matchingMessageExternalIds, setMatchingMessageExternalIds } =
    useLazoAIChat();

  //const { setConversations, deleteConversation, setArchivedConversations } = useConversations();
  const { selectedConversation, resetSelectedConversation } = useSelectedConversation();
  const [isArchivedListOpen, setIsArchivedListOpen] = useState(false);

  // Initialize mobile view properly
  useEffect(() => {
    if (isMobileView && !selectedConversation) {
      setIsConversationListOpen(true);
    }
  }, [isMobileView]);

  useEffect(() => {
    // Only hide conversation list if we have a selected conversation
    // This ensures mobile starts with conversation list visible when no conversation is selected
    if (selectedConversation) {
      setIsConversationListOpen(false);
    } else if (isMobileView) {
      // For mobile, when no conversation is selected, ensure conversation list is visible
      setIsConversationListOpen(true);
    }
    setIsArchivedListOpen(false);
    setConversationHasMembers(!!(selectedConversation && selectedConversation.members?.length > 1));
  }, [selectedConversation, isMobileView]);

  useEffect(() => {
    if (isArchivedListOpen) {
      setIsLoadingArchived(true);
      LazoChatConversationService.getArchivedByUser()
        .then((r) => {
          const list = r.getContentOrThrowError();
          //setArchivedConversations(list);
        })
        .catch((e: ApiError) => {
          NotificationService.error(e.message);
        })
        .finally(() => setIsLoadingArchived(false));
    }
  }, [isArchivedListOpen]);

  const canManageChat = !conversationHasMembers || selectedConversation?.isOwner;

  function handleSearch(searchRef: RefObject<HTMLInputElement> | undefined): () => void {
    setIsLoading(true);
    const debouncer = setTimeout(() => {
      window.sessionStorage.setItem("conversation_search", searchRef?.current?.value || "");
      if (!isConversationListOpen) {
        setIsConversationListOpen(true);
      }

      if (searchRef?.current?.value && searchRef.current?.value !== "") {
        LazoChatConversationService.search(searchRef.current!.value)
          .then((r) => {
            const list = r.getContentOrThrowError();
            //setConversations(list);
          })
          .catch((e: ApiError) => {
            NotificationService.error(e.message);
          })
          .finally(() => setIsLoading(false));
      } else {
        LazoChatConversationService.getAllOrderByCreatedAtDesc()
          .then((r) => {
            const list = r.getContentOrThrowError();
            //setConversations(list);
          })
          .catch((e: ApiError) => {
            NotificationService.error(e.message);
          })
          .finally(() => setIsLoading(false));
      }
    }, 500);

    return () => clearTimeout(debouncer);
  }

  const handleNewConversation = () => {
    setIsConversationListOpen(false);
    resetSelectedConversation();
  };

  function handleManageUsers(): void {
    setOpenManageUsersModal(true);
  }

  const openChatGuestsMenu = (event: React.MouseEvent<SVGSVGElement>) => {
    setChatGuestActionMenu(event.currentTarget);
  };

  const closeGuestChatGuestsMenu = () => {
    setChatGuestActionMenu(null);
  };

  const handleOnClickViewMembers = () => {
    setShowChatMembersModal(true);
    setChatGuestActionMenu(null);
  };

  const handleOnClickLeaveChat = () => {
    if (!selectedConversation?.externalId) return;

    LazoChatConversationService.leaveConversation(selectedConversation?.externalId).then((response) => {
      response.onSuccess((abandonedConversation) => {
        setChatGuestActionMenu(null);
        //deleteConversation(abandonedConversation);
        resetSelectedConversation();
        NotificationService.info(t("aiDrawer.messages.leftConversation", { title: abandonedConversation.title }));
      });

      response.onError((message, _) => {
        NotificationService.error(message);
      });
    });
  };

  const goToNextMessage = () => {
    if (scrollToMessageExternalId && matchingMessageExternalIds) {
      const currentIndex = matchingMessageExternalIds.indexOf(scrollToMessageExternalId);
      if (currentIndex >= 0 && currentIndex < matchingMessageExternalIds.length - 1) {
        setScrollToMessageExternalId(matchingMessageExternalIds[currentIndex + 1]);
      }
    }
  };

  const goToPreviousMessage = () => {
    if (scrollToMessageExternalId && matchingMessageExternalIds) {
      const currentIndex = matchingMessageExternalIds.indexOf(scrollToMessageExternalId);
      if (currentIndex > 0) {
        setScrollToMessageExternalId(matchingMessageExternalIds[currentIndex - 1]);
      }
    }
  };

  const handleSearchByKeyword = (searchString: string) => {
    if (!selectedConversation) return;
    LazoChatMessageService.search(selectedConversation.externalId!, searchString, null).then((response) => {
      response.onSuccess((externalIdList) => {
        setMatchingMessageExternalIds(externalIdList);
        setScrollToMessageExternalId(externalIdList[0]);
      });
    });
  };

  const handleSearchInConVersation = (searchRef: RefObject<HTMLInputElement> | undefined) => {
    if (searchRef?.current?.value && searchRef.current?.value !== "") {
      try {
        handleSearchByKeyword(searchRef.current!.value);
      } catch (e: any) {
        NotificationService.error(e.message);
      }
    }
  };

  const handleSearchByDate = (date: Date | null) => {
    if (!selectedConversation || date == null) return;
    LazoChatMessageService.search(selectedConversation.externalId!, null, date).then((response) => {
      response.onSuccess((externalIdList) => {
        if (externalIdList.length == 0) {
          NotificationService.warn(t("aiDrawer.messages.noMessagesFound"));
        }
        setMatchingMessageExternalIds(externalIdList);
        setScrollToMessageExternalId(externalIdList[0]);
      });
    });
  };

  const handleCloseFilterConversation = () => {
    setToggleFilter(false);
    setScrollToMessageExternalId(null);
    setMatchingMessageExternalIds(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <MainContainer conversationListOpen={isConversationListOpen} isMobile={isMobileView}>
        <IconsContainer className="header-controls">
          <Box sx={{ display: "flex" }}>
            <img style={{ margin: "0 0.5rem 0 0.5rem" }} src={StarLogo.src} alt="star-logo" />
            <Typography sx={{ color: Colors.LAZO_PURPLE, fontWeight: "600" }}>
              {t("aiDrawer.header.title")} / {ConversationTypePath.find((ctp) => location.pathname.includes(ctp.path))?.render || ""}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              height: "100%",
              alignItems: "center",
              gap: "0.4rem",
            }}
          >
            {isConversationListOpen || isArchivedListOpen ? (
              <>
                <Box sx={{ display: "flex", height: "90%", width: "auto" }}>
                  {!isArchivedListOpen && (
                    <NewConversationButton onClick={handleNewConversation}>
                      <AddOutlined
                        style={{
                          color: `${Colors.WHITE}`,
                          transform: "scale(1.1)",
                        }}
                      />
                    </NewConversationButton>
                  )}
                </Box>
                <ArchivedChatButton
                  onClick={() => {
                    setIsArchivedListOpen(!isArchivedListOpen);
                    setIsConversationListOpen(true);
                  }}
                  className={isArchivedListOpen ? "archived-open" : ""}
                >
                  <Inventory2Outlined sx={{ transform: "scale(1.0)" }} />
                </ArchivedChatButton>
              </>
            ) : (
              <Box sx={{ display: "flex", gap: ".75rem" }}>
                <SearchOutlined
                  onClick={() => {
                    setToggleFilter(!toggleFilter);
                  }}
                  sx={{
                    cursor: "pointer",
                    transform: "scale(1.6)",
                    "&.MuiSvgIcon-root": {
                      color: Colors.LIGHT_GRAY_CDCDCD,
                    },
                  }}
                />
                <MoreHorizOutlined onClick={openChatGuestsMenu} sx={{ transform: "scale(1.3)", cursor: "pointer" }} />
                <StyledMenu open={openMenu} anchorEl={chatGuestActionMenu} onClose={closeGuestChatGuestsMenu}>
                  <MenuItem sx={{ gap: ".5rem" }} onClick={handleOnClickViewMembers}>
                    <img src={InfoLogo.src} style={{ transform: "scale(1.2)" }} />
                    <Typography>{t("aiDrawer.menu.viewMembers")}</Typography>
                  </MenuItem>
                  {!selectedConversation?.isOwner && (
                    <MenuItem sx={{ gap: ".5rem" }} onClick={handleOnClickLeaveChat}>
                      <img src={LeaveLogo.src} style={{ transform: "scale(1.2)" }} />
                      <Typography>{t("aiDrawer.menu.leaveChat")}</Typography>
                    </MenuItem>
                  )}
                </StyledMenu>
              </Box>
            )}
            {!isMobileView && (
              <>
                <div style={{ width: "1px", backgroundColor: Colors.LIGHT_GRAY2, height: "70%", margin: "0 .75rem 0 .75rem" }} />
                <div onClick={() => setIsOpenDrawer(false)}>
                  <CloseOutlined sx={{ transform: "scale(1.1)", ":hover": { cursor: "pointer" } }} />
                </div>
              </>
            )}
          </Box>
        </IconsContainer>

        {/* Search bar */}
        {isConversationListOpen || isArchivedListOpen ? (
          <IconsContainer
            className="header-controls"
            sx={{
              marginTop: "1rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {isArchivedListOpen && (
              <Button
                sx={{
                  textTransform: "none",
                  display: "flex",
                  columnGap: "0.3rem",
                  fontWeight: 600,
                }}
                onClick={() => setIsArchivedListOpen(false)}
              >
                <ArrowBackIosIcon fontSize="large" sx={{ transform: "scale(1.3)" }} />
                {t("aiDrawer.archive.title")}
              </Button>
            )}
            <SearchField
              fullWidth
              handleSearch={handleSearch}
              displaySearchButton={false}
              defaultValue={window?.sessionStorage.getItem("conversation_search")}
              containerProps={{
                sx: { display: "flex", gap: "0.5rem", width: "100%" },
              }}
              triggerSearchOnInputChange
            />
          </IconsContainer>
        ) : (
          <div
            style={{
              display: "flex",
              height: "1%",
              width: "100%",
              margin: "0.5rem 0",
              backgroundColor: Colors.LIGHT_GRAY_DDDDDD,
            }}
          />
        )}

        <Box
          sx={{
            display: isArchivedListOpen ? "none" : "flex",
            width: "100%",
            flexFlow: "row",
            gap: "0.5rem",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isConversationListOpen ? (
            <ChatTabs variant="fullWidth" value={selectedChatsCategory} onChange={(_, val) => setSelectedChatsCategory(val)}>
              <Tab value={ChatsCategory.MY_CHATS} label={t("aiDrawer.tabs.myChats")}></Tab>
              <Tab value={ChatsCategory.GUEST_CHATS} label={t("aiDrawer.tabs.guestChats")}></Tab>
            </ChatTabs>
          ) : (
            <>
              {toggleFilter ? (
                <ConversationFilter
                  handleSearchByKeyword={handleSearchInConVersation}
                  handleSearchByDate={handleSearchByDate}
                  handleClose={handleCloseFilterConversation}
                  goToNextMessage={goToNextMessage}
                  goToPreviousMessage={goToPreviousMessage}
                  matchingMessageExternalId={matchingMessageExternalIds}
                  scrollToMessageExternalId={scrollToMessageExternalId}
                />
              ) : (
                <>
                  <Box className="back-row" sx={{ display: "flex", flex: 1, gap: "0.5rem", alignItems: "center", minWidth: 0 }}>
                    <ConversationsButton
                      onClick={() => {
                        if (selectedConversation?.isArchived) {
                          setIsArchivedListOpen(!isArchivedListOpen);
                          return;
                        }

                        setIsConversationListOpen(!isConversationListOpen);
                      }}
                    >
                      <ConversationContainer>
                        <ArrowBackIosIcon fontSize="small" />
                      </ConversationContainer>
                    </ConversationsButton>

                    {canManageChat && selectedConversation && (
                      <SecondaryButton onClick={handleManageUsers}>
                        {conversationHasMembers ? (
                          <>
                            <img src={UserManage.src} style={{ transform: "scale(1.2)" }} />
                            <Typography>{t("aiDrawer.actions.manage")}</Typography>
                          </>
                        ) : (
                          <>
                            <img src={UserPlus.src} style={{ transform: "scale(1.2)" }} />
                            <Typography>{t("aiDrawer.actions.add")}</Typography>
                          </>
                        )}
                      </SecondaryButton>
                    )}
                  </Box>
                  <ManageUserModal
                    open={openManageUsersModal}
                    handleClose={() => setOpenManageUsersModal(false)}
                    hasMembers={conversationHasMembers}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </MainContainer>

      {isArchivedListOpen ? (
        <ConversationsList archivedList loading={isLoadingArchived} chatsCategory={selectedChatsCategory} />
      ) : (
        isConversationListOpen && <ConversationsList loading={isLoading} chatsCategory={selectedChatsCategory} />
      )}

      <LazoAIChatContainer
        sx={{ display: isConversationListOpen || isArchivedListOpen ? "none !important" : "flex" }}
        lazoUser={lazoUser}
        conversationPromise={conversationPromise}
      />
      {selectedConversation && (
        <UserListModal conversation={selectedConversation} open={showChatMembersModal} handleClose={() => setShowChatMembersModal(false)} />
      )}
    </Box>
  );
}
